import { CheckCircle2, Target, Compass } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import SectionTitle from "../components/SectionTitle";
import Reveal from "../components/Reveal";

const ABOUT_IMG = "https://images.unsplash.com/photo-1573164574572-cb89e39749b4?crop=entropy&cs=srgb&fm=jpg";

const About = () => {
  const { t } = useLang();
  return (
    <div data-testid="about-page" className="pt-32">
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-sm uppercase tracking-[0.3em] font-semibold text-[#D4AF37]">{t.about.overline}</p>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0A192F] tracking-tight leading-[1.05]">
                {t.about.title}
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 text-gray-600 text-lg leading-relaxed max-w-2xl">{t.about.lead}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="relative">
                <img src={ABOUT_IMG} alt="European professionals" className="w-full h-[420px] object-cover" loading="lazy" />
                <div className="absolute -bottom-6 -left-6 bg-[#D4AF37] text-[#0A192F] py-4 px-6 hidden md:block">
                  <p className="text-[10px] uppercase tracking-[0.3em] font-semibold">Valencia</p>
                  <p className="font-serif text-xl">Spain · Europe</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="bg-gray-100 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          <Reveal>
            <div data-testid="about-mission" className="bg-white p-10 border border-gray-200 h-full">
              <Target className="w-8 h-8 text-[#D4AF37]" />
              <h3 className="mt-6 font-serif text-2xl sm:text-3xl text-[#0A192F]">{t.about.mission_t}</h3>
              <p className="mt-4 text-gray-600 leading-relaxed">{t.about.mission_d}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div data-testid="about-vision" className="bg-[#0A192F] text-white p-10 h-full">
              <Compass className="w-8 h-8 text-[#D4AF37]" />
              <h3 className="mt-6 font-serif text-2xl sm:text-3xl">{t.about.vision_t}</h3>
              <p className="mt-4 text-gray-300 leading-relaxed">{t.about.vision_d}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionTitle overline="Core" title={t.about.values_t} />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-gray-200 border border-gray-200">
            {t.about.values.map((v, i) => (
              <Reveal key={v} delay={i * 0.05}>
                <div data-testid={`about-value-${i}`} className="bg-white p-8 h-full">
                  <CheckCircle2 className="w-6 h-6 text-[#D4AF37]" />
                  <p className="mt-5 font-serif text-xl text-[#0A192F]">{v}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="bg-[#0A192F] text-white py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.3em] font-semibold text-[#D4AF37]">Approach</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight">{t.about.approach_t}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg text-gray-300 leading-relaxed">{t.about.approach_d}</p>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default About;
